/**
 * Employee detail — Payroll Increment 1.
 *
 * Mirrors the standard detail-page chrome (invoice / bill detail): sticky
 * `.composer-head` at the top, max-width-constrained `.detail-main` in the
 * middle, sticky `.composer-foot` summary band at the bottom. Page-scoped
 * CSS lives in `features/employees/components/employee-detail.css`.
 *
 * @see prototype/payroll/employee-detail.html
 */
import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Pencil } from "lucide-react";
import { Alert, ConfirmDialog } from "@flint/ui";
import { paiseToInr, paiseToInrShort, formatDate } from "../../../../lib/format.ts";
import { trpc } from "../../../../lib/trpc.ts";
import { EmployeeAvatar } from "../../../../features/employees/index.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import "../../../../features/employees/components/employee-detail.css";

export const Route = createFileRoute("/(workspace)/payroll/employees/$employeeId/")({
  component: EmployeeDetailRoute,
});

const PAY_MODE_LABEL: Record<string, string> = {
  neft: "Bank transfer (NEFT)",
  upi: "UPI",
  cheque: "Cheque",
};

function EmployeeDetailRoute() {
  const baseCrumbs = [
    { label: "Payroll", to: "/payroll/employees" },
    { label: "Employees", to: "/payroll/employees" },
  ];
  return (
    <WorkspaceShell
      breadcrumbs={[...baseCrumbs, { label: "Detail" }]}
      // Route owns its own scroll surface — the head + foot are fixed flex
      // children, only `.detail-main` scrolls. `disableViewportScroll`
      // hands us a plain `flex flex-1 min-h-0 overflow-hidden` block so
      // there's no Radix ScrollArea wrapper collapsing our flex height.
      disableViewportScroll
      contentClassName="max-w-none h-full"
    >
      <EmployeeDetailPage />
    </WorkspaceShell>
  );
}

function EmployeeDetailPage() {
  const { employeeId } = Route.useParams();
  const utils = trpc.useUtils();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const employee = trpc.employees.get.useQuery({ id: employeeId });
  const team = trpc.team.list.useQuery({});

  const setStatus = trpc.employees.setStatus.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.employees.list.invalidate(),
        utils.employees.counts.invalidate(),
        utils.employees.get.invalidate({ id: employeeId }),
      ]);
    },
  });

  if (employee.isLoading) {
    return <p className="p-6 text-sm text-muted">Loading employee…</p>;
  }
  if (employee.error) {
    return (
      <div className="p-6">
        <Alert intent="bad" title="Couldn't load employee" description={employee.error.message} />
      </div>
    );
  }
  const e = employee.data;
  if (!e) return null;

  const linkedMember = e.membershipId
    ? team.data?.members.find((m) => m.membershipId === e.membershipId)
    : undefined;

  const annualGross = (BigInt(e.monthlyGrossPaise) * 12n).toString();
  const isActive = e.status === "active";

  return (
    <div className="employee-detail-page">
      {/* ── Sticky head ──────────────────────────────────────────── */}
      <header className="composer-head">
        <Link to="/payroll/employees" className="composer-head-back" aria-label="Back to employees">
          <ArrowLeft size={16} strokeWidth={2.2} />
        </Link>
        <div className="composer-head-text">
          <EmployeeAvatar name={e.name} photoUrl={e.photoUrl} size="md" />
          <div className="head-meta">
            <h1>
              <span>{e.name}</span>
              <span className={`head-pill ${isActive ? "ok" : "muted"}`}>
                {isActive ? "Active" : "Inactive"}
              </span>
            </h1>
            <p className="sub">
              <strong>{e.designation ?? "—"}</strong>
              {e.employeeCode ? (
                <>
                  <span className="sep" aria-hidden>·</span>
                  <span className="num">{e.employeeCode}</span>
                </>
              ) : null}
              <span className="sep" aria-hidden>·</span>
              {linkedMember ? (
                <span className="head-pill sky">{linkedMember.role} login</span>
              ) : (
                <span className="head-pill muted">No login</span>
              )}
            </p>
          </div>
        </div>
        <div className="composer-head-actions">
          <Link
            to="/payroll/employees/$employeeId/edit"
            params={{ employeeId }}
            className="inline-flex h-10 items-center gap-2 rounded-sm border border-line bg-paper px-4 text-sm font-semibold text-ink hover:bg-bg"
          >
            <Pencil size={15} aria-hidden />
            Edit
          </Link>
        </div>
      </header>

      {/* ── Middle (own scroll surface, constrained max-width) ─────────── */}
      <div className="detail-main">
        <div className="detail-main-inner grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
          {/* Main column */}
          <div className="space-y-4">
            <Card title="Compensation" meta="used to pre-fill payroll runs">
              <KvRows
                rows={[
                  ["Monthly gross", paiseToInr(e.monthlyGrossPaise), true],
                  ["Annual (12×)", paiseToInr(annualGross), true],
                  [
                    "Default deductions",
                    `PF ${paiseToInr(e.defaultPfPaise)} · PT ${paiseToInr(e.defaultPtPaise)} — TDS typed per run`,
                    false,
                  ],
                  ["Pay mode", PAY_MODE_LABEL[e.payMode] ?? e.payMode, false],
                ]}
              />
            </Card>

            <Card title="Identity & bank" meta="PAN & account encrypted at rest">
              <KvRows
                rows={[
                  ["PAN", e.pan ?? "Not on file", true],
                  ["Aadhaar", e.aadhaar ?? "Not on file", true],
                  ["Date of joining", formatDate(e.dateOfJoining), true],
                  ["Bank", e.bankName ?? "Not on file", false],
                  [
                    "Account",
                    e.bankAccount
                      ? e.hasBankAccount && e.bankAccount.length <= 4
                        ? `··········${e.bankAccount}`
                        : e.bankAccount
                      : "Not on file",
                    true,
                  ],
                  ["IFSC", e.bankIfsc ?? "Not on file", true],
                ]}
              />
            </Card>

            <Card title="Payslip history" meta="FY 2026–27">
              <div className="rounded-md border border-dashed border-line bg-raised p-8 text-center">
                <p className="text-sm font-semibold text-ink">No payslips yet</p>
                <p className="mt-1 text-sm text-muted">
                  Payroll runs land in the Payroll module — each monthly run will generate a payslip
                  here.
                </p>
              </div>
            </Card>
          </div>

          {/* Right rail */}
          <div className="space-y-4">
            <Card title="App access">
              {linkedMember ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <EmployeeAvatar name={linkedMember.user.name} size="sm" />
                    <div>
                      <div className="text-sm font-semibold text-ink">Linked to Flint login</div>
                      <div className="text-xs text-muted">
                        {linkedMember.role} · {linkedMember.user.email ?? linkedMember.user.phone}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-muted">
                    This employee also signs into Flint. App role and payroll are managed
                    separately — changing one doesn't affect the other.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted">
                  Not linked to a Flint login. Most employees don't sign in. Link one from{" "}
                  <Link
                    to="/payroll/employees/$employeeId/edit"
                    params={{ employeeId }}
                    className="font-semibold text-sky-700 hover:underline"
                  >
                    Edit
                  </Link>
                  .
                </p>
              )}
            </Card>

            <Card title="Danger zone">
              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                className="inline-flex h-10 w-full items-center justify-center rounded-sm border border-bad/40 bg-bad-50 px-4 text-sm font-semibold text-bad hover:bg-bad-50/80"
              >
                {isActive ? "Mark inactive" : "Reactivate"}
              </button>
              <ConfirmDialog
                open={confirmOpen}
                onOpenChange={setConfirmOpen}
                intent={isActive ? "danger" : "default"}
                title={isActive ? "Mark this employee inactive?" : "Reactivate this employee?"}
                description={
                  isActive
                    ? "Inactive employees are excluded from new payroll runs but keep their history. You can reactivate them anytime."
                    : "Reactivated employees are included in new payroll runs again."
                }
                confirmLabel={isActive ? "Mark inactive" : "Reactivate"}
                pending={setStatus.isPending}
                onConfirm={async () => {
                  await setStatus.mutateAsync({
                    id: employeeId,
                    status: isActive ? "inactive" : "active",
                  });
                }}
              />
              <p className="mt-2 text-xs text-muted">
                {isActive
                  ? "Excluded from new payroll runs; history is preserved."
                  : "Currently excluded from new payroll runs."}
              </p>
              {setStatus.error ? (
                <div className="mt-3">
                  <Alert
                    intent="bad"
                    title="Couldn't update status"
                    description={setStatus.error.message}
                  />
                </div>
              ) : null}
            </Card>
          </div>
        </div>
      </div>

      {/* ── Sticky foot — summary anchor ───────────────────────── */}
      <footer className="composer-foot" aria-label="Detail summary">
        <div className="composer-foot-context">
          <span className={`status-tag ${isActive ? "ok" : ""}`}>
            {isActive ? "Active" : "Inactive"}
          </span>
          <span aria-hidden className="sep">·</span>
          <strong>{e.name}</strong>
          {e.designation ? (
            <>
              <span aria-hidden className="sep">·</span>
              <span>{e.designation}</span>
            </>
          ) : null}
          {e.employeeCode ? (
            <>
              <span aria-hidden className="sep">·</span>
              <span className="num">{e.employeeCode}</span>
            </>
          ) : null}
        </div>
        <div className="composer-foot-amount">
          <span className="caption">monthly gross</span>
          <span className="amount num">{paiseToInrShort(e.monthlyGrossPaise)}</span>
        </div>
      </footer>
    </div>
  );
}

// ── presentational helpers ───────────────────────────────────────

function Card({
  title,
  meta,
  children,
}: {
  title: string;
  meta?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
      <header className="flex items-center justify-between gap-2 border-b border-line px-4 py-3">
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        {meta ? <span className="text-xs text-muted">{meta}</span> : null}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function KvRows({ rows }: { rows: Array<[string, string, boolean]> }) {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-3">
      {rows.map(([k, v, mono]) => (
        <div key={k} className="contents">
          <dt className="text-sm text-muted">{k}</dt>
          <dd
            className={mono ? "text-right font-mono text-sm text-ink" : "text-right text-sm text-ink"}
          >
            {v}
          </dd>
        </div>
      ))}
    </dl>
  );
}
